import { CalendarDays, MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { PageHero, SectionHead, DoodleBand, LightSection } from "@/components/site/Shared";
import { SponsorPackages, TicketTiers } from "@/components/site/Offerings";
import { LeadForm } from "@/components/site/LeadForm";
import { ScheduleBlock } from "@/components/site/ScheduleBlock";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BASE = [
  { name: "name", label: "Full name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone" },
];
const VENDOR = [...BASE,
  { name: "business", label: "Business / brand name", required: true },
  { name: "category", label: "What you sell", type: "select", required: true, options: ["Books & zines", "Art & prints", "Apparel", "Food & drink", "Other"] },
  { name: "website", label: "Website or Instagram" },
  { name: "message", label: "Tell us about your booth", type: "textarea" },
];
const PERFORMER = [...BASE,
  { name: "stage_name", label: "Stage name" },
  { name: "style", label: "Style", type: "select", options: ["Spoken word", "Page poetry", "Slam", "Music + poetry", "Other"] },
  { name: "sample_link", label: "Link to a sample performance", full: true },
  { name: "message", label: "A few lines about you and your work", type: "textarea", required: true },
];
const TAB = "rounded-full data-[state=active]:bg-ink data-[state=active]:text-shell";

export default function DefStreet() {
  const { settings: s } = useSite() || {};
  if (!s) return <div className="h-[80vh]" />;
  return (
    <div data-testid="def-street-page">
      <PageHero testid="def-street-hero" eyebrow="annual festival" title="Def Street Poetry Festival" intro={s.defstreet_intro} image={s.images?.defstreet}>
        <a href="#tickets" className="btn-terra" data-testid="def-street-tickets-link">Tickets &gt;</a>
        <a href="#apply" className="btn-line" data-testid="def-street-apply-link">Perform or vend</a>
      </PageHero>
      <section className="bg-tang text-ink">
        <div className="container-mas py-5 flex flex-col sm:flex-row gap-3 sm:gap-12 text-sm font-medium">
          <span className="flex items-center gap-2" data-testid="def-street-date"><CalendarDays className="w-4 h-4" />{s.defstreet_date}</span>
          <span className="flex items-center gap-2" data-testid="def-street-location"><MapPin className="w-4 h-4" />{s.defstreet_location}</span>
        </div>
      </section>
      <section id="tickets" className="container-mas py-16 sm:py-20 scroll-mt-24">
        <SectionHead eyebrow="tickets" title="Grab your pass" className="mb-10" />
        <TicketTiers />
      </section>
      <DoodleBand />
      <section className="container-mas py-16 sm:py-20">
        <SectionHead eyebrow="sponsorship packages" title="Sponsor Def Street" className="mb-10" />
        <SponsorPackages category={["Def Street"]} />
      </section>
      <section className="container-mas pb-16 sm:pb-20">
        <SectionHead eyebrow="festival schedule" title="On the calendar" className="mb-8" />
        <ScheduleBlock category={["Def Street"]} />
      </section>
      <LightSection id="apply" className="scroll-mt-20">
        <div className="container-mas py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHead light eyebrow="applications" title="Take the stage, or set up shop" /></div>
          <div className="lg:col-span-8 bg-white p-6 sm:p-8 shadow-[10px_10px_0_#ED773C]">
            <Tabs defaultValue="performer">
              <TabsList className="bg-shell rounded-full p-1 mb-6">
                <TabsTrigger value="performer" className={TAB} data-testid="def-street-tab-performer">Performer</TabsTrigger>
                <TabsTrigger value="vendor" className={TAB} data-testid="def-street-tab-vendor">Vendor</TabsTrigger>
              </TabsList>
              <TabsContent value="performer"><LeadForm type="performer_application" fields={PERFORMER} submitLabel="Send performer application" successText="Application received — thank you!" /></TabsContent>
              <TabsContent value="vendor"><LeadForm type="vendor_application" fields={VENDOR} submitLabel="Send vendor application" successText="Vendor application received!" /></TabsContent>
            </Tabs>
          </div>
        </div>
      </LightSection>
    </div>
  );
}
